import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Plus, Trash2, ExternalLink } from "lucide-react";

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", color: "#E63946" });

  const load = async () => {
    const { data } = await api.get("/projects");
    setProjects(data);
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/projects", form);
    setForm({ name: "", description: "", color: "#E63946" });
    setOpen(false);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Fold this project? All tasks will be discarded.")) return;
    await api.delete(`/projects/${id}`);
    load();
  };

  return (
    <div className="p-8 lg:p-12">
      <header className="flex items-end justify-between mb-10">
        <div>
          <div className="label-mono text-green-400 mb-2">// projects.list</div>
          <h1 className="font-serif text-4xl lg:text-5xl tracking-tighter" data-testid="projects-title">Projects</h1>
          <p className="text-neutral-400 text-sm mt-2">Each project is a table. Each table runs its own hands.</p>
        </div>
        <button data-testid="projects-new-button" onClick={() => setOpen(true)} className="btn-primary btn-glitch flex items-center gap-2">
          <Plus size={14} /> New Project
        </button>
      </header>

      {open && (
        <form onSubmit={create} data-testid="projects-create-form" className="panel panel-accent-red p-6 mb-8 deal">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label>
              <span className="label-mono block mb-2">Name</span>
              <input data-testid="project-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-shell w-full px-3 py-2 text-sm font-jet" placeholder="Operation Royal Flush" />
            </label>
            <label className="md:col-span-2">
              <span className="label-mono block mb-2">Description</span>
              <input data-testid="project-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-shell w-full px-3 py-2 text-sm font-jet" placeholder="Pwn the quarter, take the pot." />
            </label>
          </div>
          <div className="flex items-center gap-3 mt-5">
            <button type="submit" data-testid="project-create-submit" className="btn-primary btn-glitch">Create</button>
            <button type="button" onClick={() => setOpen(false)} data-testid="project-create-cancel" className="btn-ghost">Cancel</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="projects-grid">
        {projects.length === 0 && (
          <div className="panel p-10 col-span-full text-center deal">
            <div className="font-serif text-2xl mb-2">No projects on the felt.</div>
            <div className="text-sm text-neutral-500">Click <span className="text-amber-400">New Project</span> to ante up.</div>
          </div>
        )}
        {projects.map((p, i) => (
          <div key={p.id} className="panel p-6 card-suit deal group" data-suit={["♠","♥","♦","♣"][i % 4]} data-testid={`project-card-${p.id}`} style={{ animationDelay: `${i * 50}ms` }}>
            <div className="flex items-start justify-between mb-3">
              <div className="w-8 h-8 border border-neutral-800" style={{ background: p.color }} />
              <button data-testid={`project-delete-${p.id}`} onClick={() => remove(p.id)} className="opacity-0 group-hover:opacity-100 text-neutral-500 hover:text-red-400 transition">
                <Trash2 size={14} />
              </button>
            </div>
            <div className="font-serif text-2xl mb-1 tracking-tight">{p.name}</div>
            <p className="text-sm text-neutral-400 min-h-[2.5rem]">{p.description || "—"}</p>
            <div className="mt-6 flex items-center justify-between">
              <span className="label-mono">{p.status}</span>
              <Link data-testid={`project-open-${p.id}`} to={`/app/projects/${p.id}`} className="text-amber-400 font-jet text-xs uppercase tracking-[0.18em] flex items-center gap-1 hover:text-amber-300">
                Open Table <ExternalLink size={12} />
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
